import PropTypes from "prop-types";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ACL_RELATIONSHIPS from "acl-relationships";
import { FormRecordPicker } from "components/BaseForm";
import BaseForm from "components/BaseForm/BaseForm";
import ConfirmDialog from "components/modals/ConfirmDialog";
import { formatError } from "helpers/formatError";
import useAuthorized from "hooks/useAuthorized";
import { swoogoRegistrantKeys } from "routes/swoogo/queryKeys";
import ContactService from "services/Contact";
import SwoogoService from "services/Swoogo";

const REQUIRED_CONTACT_SCHEMA = z.object({
  contact_id: z
    .number({
      required_error: "Select a Contact",
      invalid_type_error: "Select a Contact",
    })
    .int(),
});

// A role that may unlink saves an empty Contact to remove the link.
const OPTIONAL_CONTACT_SCHEMA = z.object({
  contact_id: z.number().int().nullable(),
});

const fetchContacts = search =>
  ContactService.getContactsForFilters(
    search ? { search_query: search } : null
  );

/**
 * Controlled dialog that links one Swoogo registrant to a Pleteo Contact, or
 * removes the link for a role that may unlink. Replacing or removing an
 * existing link is confirmed before it is saved. Nothing is sent to Swoogo.
 */
const LinkContactDialog = ({ registrant, open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const canUnlink = useAuthorized([ACL_RELATIONSHIPS.swoogo.unlinkContact]);
  const [pendingChange, setPendingChange] = useState(null);
  const currentContactId = registrant.contact_id ?? null;
  const hasContact = currentContactId !== null;
  const registrantLabel = registrant.name || registrant.email;

  const saveMutation = useMutation({
    mutationFn: contactId =>
      contactId === null
        ? SwoogoService.unlinkRegistrantContact(registrant.id)
        : SwoogoService.linkRegistrantContact(registrant.id, contactId),
    onSuccess: (_data, contactId) => {
      queryClient.invalidateQueries({
        queryKey: swoogoRegistrantKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: swoogoRegistrantKeys.detail(registrant.id),
      });
      toast.success(contactId === null ? "Contact unlinked" : "Contact linked");
      setPendingChange(null);
      onOpenChange(false);
    },
    onError: (err, contactId) => {
      setPendingChange(null);
      toast.error(
        formatError(
          err,
          contactId === null
            ? "Could not unlink the Contact"
            : "Could not link the Contact"
        )
      );
    },
  });

  // Save is disabled while the value is unchanged, so with a current Contact
  // every submit replaces or removes it.
  const handleSubmit = ({ contact_id: contactId }) => {
    if (hasContact) {
      setPendingChange({ contactId });
      return undefined;
    }
    return saveMutation.mutateAsync(contactId);
  };

  const isUnlinkPending = pendingChange?.contactId === null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {hasContact ? "Change Contact" : "Link Contact"}
            </DialogTitle>
          </DialogHeader>
          <BaseForm
            schema={
              canUnlink ? OPTIONAL_CONTACT_SCHEMA : REQUIRED_CONTACT_SCHEMA
            }
            defaultValues={{ contact_id: currentContactId }}
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
            shouldDisableSubmit={values =>
              values.contact_id === currentContactId
            }
          >
            <FormRecordPicker
              name="contact_id"
              label="Contact"
              required={!canUnlink}
              fetchOptions={fetchContacts}
              optionsKey="swoogo-link-contact"
              display={
                hasContact
                  ? {
                      id: registrant.contact_id,
                      label: registrant.contact_name,
                    }
                  : undefined
              }
              placeholder="Search Contacts…"
            />
          </BaseForm>
        </DialogContent>
      </Dialog>
      {pendingChange && (
        <ConfirmDialog
          open
          onOpenChange={() => setPendingChange(null)}
          title={isUnlinkPending ? "Unlink Contact" : "Replace Contact"}
          description={
            isUnlinkPending
              ? `${registrantLabel} will no longer be linked to ${registrant.contact_name}, and later syncs will not link it again. Swoogo is not changed.`
              : `${registrantLabel} is linked to ${registrant.contact_name}. Replacing the link moves this registrant to the selected Contact. Swoogo is not changed.`
          }
          confirmText={isUnlinkPending ? "Unlink" : "Replace"}
          cancelVariant="ghost"
          onConfirm={() => saveMutation.mutate(pendingChange.contactId)}
        />
      )}
    </>
  );
};

LinkContactDialog.propTypes = {
  registrant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    email: PropTypes.string,
    contact_id: PropTypes.number,
    contact_name: PropTypes.string,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};

export default LinkContactDialog;
